import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Flex,
  Box,
  Text,
  Select,
  Input,
  Skeleton,
  SkeletonText,
  Divider,
} from "@chakra-ui/react";
import { fetchDataByDateAction } from "state/actions/analyticsActions";
import Chart from "react-apexcharts";
import ExportButton from "components/Reports/ExportCSV";

const tables = [
  "users",
  "packages",
  "deliveries",
  "claims",
  "transactions",
  "drivers",
  "businesses",
  "vehicles",
  "reviews",
  "help_topics",
  "chats",
  "faqs",
  "notifications",
];

const DataByDateBarChart = ({
  title = "Data Overview",
  showExportButton = true,
  fileName = "DataByDateChart",
}) => {
  const dispatch = useDispatch();
  const [selectedTable, setSelectedTable] = useState("packages");

  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 15);

  const formatDateInput = (date) => date.toISOString().split("T")[0]; 

  const [startDate, setStartDate] = useState(formatDateInput(thirtyDaysAgo));
  const [endDate, setEndDate] = useState(formatDateInput(today));

  const { dataByDate, loading, error } = useSelector((state) => state.analytics);

  const getDaysDifference = (start, end) => {
    const startD = new Date(start);
    const endD = new Date(end);
    const diffTime = endD - startD;
    if (diffTime < 0) return 0;
    return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  useEffect(() => {
    const days = getDaysDifference(startDate, endDate);
    if (days <= 0) return;

    dispatch(fetchDataByDateAction(selectedTable, days));
  }, [dispatch, selectedTable, startDate, endDate]);

  const transformData = (data) => {
    if (!data || data.length === 0) return { labels: [], values: [] };

    const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
    const rangeDays = getDaysDifference(startDate, endDate);
    const slicedData = sortedData.slice(-rangeDays);

    const labels = slicedData.map((item) => {
      const date = new Date(item.date);
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    });

    const values = slicedData.map((item) => item.count);
    return { labels, values };
  };

  const chartData = transformData(dataByDate?.[selectedTable]);

  const chartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    xaxis: {
      title: { text: "Date", style: { color: "black", fontSize: "14px" } },
      categories: chartData.labels,
      labels: { style: { colors: "black", fontSize: "10px" } },
    },
    yaxis: {
      title: { text: "Count", style: { color: "black", fontSize: "14px" } },
      labels: { style: { colors: "black", fontSize: "12px" }, offsetX: 10, maxWidth: 60 },
    },
    legend: { show: false },
    grid: { show: false },
    tooltip: { theme: "dark" },
    plotOptions: {
      bar: { distributed: true, borderRadius: 3, borderRadiusApplication: "end", columnWidth: "50%" },
    },
    fill: {
      colors: ["#071D54", "#102A70", "#1A388C", "#2445A8", "#2D52C4", "#376FE0", "#418CFC", "#5AA0FF"],
    },
  };

  if (loading) return <LoadingSkeleton />;
  if (error) return <Text color="red.500">Error loading data: {error}</Text>;

  return (
    <Flex
      w="100%"
      maxW="600px"
      h="100%"
      boxShadow="0px 4px 24px 0px #0000001F"
      borderRadius="12px"
      py={4}
      px={5}
      direction="column"
    >
      {/* Top row: Select + Export */}
      <Flex justify="space-between" alignItems="center" mb={2} w="100%">
        <Select
          w="200px"
          value={selectedTable}
          onChange={(e) => setSelectedTable(e.target.value)}
        >
          {tables.map((table) => (
            <option key={table} value={table}>
              {table.charAt(0).toUpperCase() + table.slice(1)}
            </option>
          ))}
        </Select>

        {showExportButton && <ExportButton data={chartData} fileName={fileName} />}
      </Flex>

      {/* Date selectors BELOW select */}
      <Flex gap={2} align="center" w="100%" mb={4} flexWrap="wrap">
        <Text>From:</Text>
        <Input
          type="date"
          max={endDate}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          maxW="200px"
        />
        <Text>To:</Text>
        <Input
          type="date"
          min={startDate}
          max={formatDateInput(today)}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          maxW="200px"
        />
      </Flex>

      <Chart
        key={`${selectedTable}_${startDate}_${endDate}`} 
        options={chartOptions}
        series={[{ name: title, data: chartData.values }]}
        type="bar"
        width="100%"
        height="400px"
      />
    </Flex>
  );
};

const LoadingSkeleton = () => (
  <Flex w="100%">
    <Box w="100%" mt={6}>
      <Skeleton w="40%" mb={4} height="4" />
      <SkeletonText noOfLines={6} spacing="4" skeletonHeight="4" />
    </Box>
  </Flex>
);

export default DataByDateBarChart;